import React, { useEffect, useState } from 'react';
import ContentLayout from '../../layout/ContentLayout';
import { useAuth } from '../../context/useAuth';
import '../../styles/clinic-medic.scss';
import TableSection from '../../components/tables/TableSection';
import { Link, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { formatPriceWithCurrency } from '../../utils/formatPrice';
import Search from 'antd/es/transfer/search';
import { Button, ConfigProvider, DatePicker, Dropdown } from 'antd';
import {
  createReferredDoctor,
  exportMedicOrders,
  fetchAllClinics,
  fetchMedic,
  fetchSingleClinic,
  getClinicMedics,
  // Clinic referral-commission awarding disabled (2026-08-17): clinics no longer
  // earn a cut, so getClinicOrders is commented out too (see api/medics-api.js).
  // getClinicOrders,
  getClinicReferralLink,
  getClinicSummary,
  getDoctorReferralChain,
  getDoctorReferralLink,
  getDoctorSummary,
  getMedicOrders,
} from '../../api/medics-api';
import { useDebounce } from '../../hooks/useDebounce';
import { usePermissions } from '../../hooks/usePermissions';
import CreateMedicModal from '../../components/modals/CreateDoctorModal';
import DoctorReferralModal from '../../components/modals/DoctorReferralModal';
import DirectReferralsModal from '../../components/modals/DirectReferralsModal';
import DoctorCharityEarnings from '../../components/doctor/DoctorCharityEarnings';
import MentorLinkedAgents from '../../components/doctor/MentorLinkedAgents';
import { toast, ToastContainer } from 'react-toastify';
import {
  PAYOUT_STATUSES,
  formatPayoutStatusAsPill,
} from '../../utils/formatPayoutStatus';

const { RangePicker } = DatePicker;

const payoutStatusOptions = [
  { label: 'Na čekanju', value: 'pending' },
  { label: 'Realizovano', value: 'paid' },
];

// TODO: Replace with the real data
const mock_data = [
  {
    id: 1,
    _id: 1,
    firstName: 'Ime',
    lastName: 'Prezime',
    email: 'example@gmail.com',
    name: 'Proizvod 1',
    date: new Date(),
    totalPrice: 4290,
    commission: 429,
  },
  {
    id: 2,
    _id: 2,
    firstName: '',
    lastName: '',
    email: '',
    name: 'Proizvod 2',
    date: new Date(),
    totalPrice: 5290,
    commission: 529,
  },
  {
    id: null,
    _id: null,
    firstName: 'Ime',
    lastName: 'Prezime',
    email: 'example@gmail.com',
    name: 'Proizvod 3',
    date: new Date(),
    totalPrice: 6290,
    commission: 629,
  },
  {
    id: 4,
    _id: 4,
    firstName: 'Ime',
    lastName: 'Prezime',
    email: 'example@gmail.com',
    name: '',
    date: null,
    totalPrice: null,
    commission: null,
  },
  {
    id: 5,
    _id: 5,
    firstName: 'Ime',
    lastName: 'Prezime',
    email: 'example@gmail.com',
    name: 'Proizvod 5',
    date: new Date(),
    totalPrice: 8290,
    commission: 829,
  },
];
const ClinicMedic = (props) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Mentors with users:read can open their own clients' page (the API checks the network).
  const { can: canDo } = usePermissions();

  const [items, setItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(1);
  const [isCreateMedicModalOpen, setIsCreateMedicModalOpen] = useState(false);
  const [isCreateMentorModalOpen, setIsCreateMentorModalOpen] = useState(false);
  const [done, setDone] = useState(false);
  // const [allClinics, setAllClinics] = useState([]);
  const [clinicId, setClinicId] = useState(null);
  const [clinic, setClinic] = useState(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);
  const [userSummary, setUserSummary] = useState(null);
  const [userReferral, setUserReferral] = useState(null);
  const [isReferralModalOpen, setIsReferralModalOpen] = useState(false);
  const [isDirectReferralsModalOpen, setIsDirectReferralsModalOpen] =
    useState(false);
  const [doctorSelf, setDoctorSelf] = useState(null);
  const [doctorCommissionPercent, setDoctorCommissionPercent] = useState(null);
  const [orderFilter, setOrderFilter] = useState({
    createdFrom: null,
    createdTo: null,
    payoutStatus: null,
  });
  // Clinic referral-commission awarding disabled (2026-08-17): clinic's own
  // commission-ledger orders section commented out below.
  // const [clinicOrders, setClinicOrders] = useState([]);
  // const [currentPageClinicOrders, setCurrentPageClinicOrders] = useState(1);
  // const [totalPagesClinicOrders, setTotalPagesClinicOrders] = useState(1);

  const handleDateRangeChange = (dates) => {
    setOrderFilter((prev) => ({
      ...prev,
      createdFrom: dates?.[0] ? dates[0].startOf('day').toISOString() : null,
      createdTo: dates?.[1] ? dates[1].endOf('day').toISOString() : null,
    }));
    setCurrentPage(1);
    // Clinic referral-commission awarding disabled (2026-08-17): clinic orders
    // pagination removed above.
    // setCurrentPageClinicOrders(1);
  };

  const activePayoutStatusLabel = orderFilter.payoutStatus
    ? payoutStatusOptions.find((o) => o.value === orderFilter.payoutStatus)
        ?.label
    : null;

  const handlePayoutStatusSelect = (value) => {
    setOrderFilter((prev) => ({ ...prev, payoutStatus: value }));
    setCurrentPage(1);
    // setCurrentPageClinicOrders(1);
  };

  const handleClearPayoutStatus = (e) => {
    e.stopPropagation();
    setOrderFilter((prev) => ({ ...prev, payoutStatus: null }));
    setCurrentPage(1);
    // setCurrentPageClinicOrders(1);
  };

  const handleOpenReferralModal = async () => {
    const { data, success } = await fetchMedic(user._id);
    if (success) {
      setDoctorSelf(data);
    }
    setIsReferralModalOpen(true);
  };

  const onSearchUsers = (value) => {
    setSearch(value);
  };

  const handleCreateMedicModalClose = () => {
    setIsCreateMedicModalOpen(false);
    setDone(false);
  };

  const handleCreateMentorModalClose = () => {
    setIsCreateMentorModalOpen(false);
    setDone(false);
  };

  const handleTableAction = async () => {
    switch (user.role) {
      case 'doctor':
        const { data, success, message } = await exportMedicOrders(user._id);
        if (success) {
          const blob = new Blob([data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          const file = new File([blob], 'export.csv', {
            type: blob.type,
            lastModified: new Date(),
          });

          const link = document.createElement('a');
          link.href = URL.createObjectURL(file);
          link.download = file.name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(link.href);
        } else {
          console.error(message || 'Error while exporting the table.');
        }
        return;
      case 'clinic_owner':
        setIsCreateMedicModalOpen(true);
        return;
      default:
        console.log('default switch state');
        return;
    }
  };

  const clinic_table_columns = [
    {
      key: 'firstName',
      label: 'Ime mentora',
      render: (_, row) => {
        const fullName =
          !row?.doctor?.firstName && !row?.doctor?.lastName
            ? '-'
            : `${row?.doctor?.firstName || ''} ${row?.doctor?.lastName || ''}`;
        return fullName;
      },
    },
    {
      key: 'email',
      label: 'Email',
      render: (_, row) => {
        return row?.doctor?.email || '-';
      },
    },
    {
      key: 'orders',
      label: 'Ukupno kupovina',
      render: (_, row) => {
        return row?.orders;
      },
    },
    {
      key: 'revenue',
      label: 'Vrednost',
      render: (value) => {
        return formatPriceWithCurrency(value || 0);
      },
    },
    {
      key: 'doctorCommission',
      label: 'Zarada mentora',
      render: (value) => {
        return formatPriceWithCurrency(value || 0);
      },
    },
    // Clinic referral-commission awarding disabled (2026-08-17): clinics no
    // longer earn a commission cut.
    // {
    //   key: 'clinicCommission',
    //   label: 'Zarada organizacije',
    //   render: (value) => {
    //     return formatPriceWithCurrency(value || 0);
    //   },
    // },
  ];

  // Clinic referral-commission awarding disabled (2026-08-17): clinic's own
  // commission-ledger orders table commented out below.
  // const clinic_orders_columns = [
  //   {
  //     key: 'firstName',
  //     label: 'Ime kupca',
  //     render: (_, row) => row?.customer?.fullName || '-',
  //   },
  //   {
  //     key: 'email',
  //     label: 'Email',
  //     render: (_, row) => row?.customer?.email || '-',
  //   },
  //   {
  //     key: 'totalPrice',
  //     label: 'Osnovica za proviziju',
  //     render: (_, row) => formatPriceWithCurrency(row.commissionBase || 0),
  //   },
  //   {
  //     key: 'commissionClinic',
  //     label: 'Provizija organizacije',
  //     render: (_, row) => formatPriceWithCurrency(row.commissionClinic || 0),
  //   },
  //   {
  //     key: 'percent',
  //     label: 'Procenat provizije',
  //     render: (val) => (val != null ? `${val}%` : '-'),
  //   },
  //   {
  //     key: 'chainOwnerName',
  //     label: 'Mentor',
  //     render: (val) => val || '-',
  //   },
  //   {
  //     key: 'payoutStatus',
  //     label: 'Status provizije',
  //     render: (val, row) => formatPayoutStatusAsPill(row),
  //   },
  //   {
  //     key: 'createdAt',
  //     label: 'Datum kupovine',
  //     render: (value) => {
  //       const date_render = dayjs(value).isValid()
  //         ? dayjs(value).format('MM.DD.YYYY')
  //         : null;
  //       return date_render ? `${date_render}.` : '-';
  //     },
  //   },
  // ];

  const medic_table_columns = [
    {
      key: 'firstName',
      label: 'Ime kupca',
      render: (_, row) => {
        const name = row?.customer?.fullName || '-';
        return row?.customerId && canDo('users:read') ? (
          <Link
            to={`/korisnik/detalji/${row.customerId}`}
            state={{ firstName: name, from: '/clinic-medic' }}
          >
            {name}
          </Link>
        ) : (
          name
        );
      },
    },
    /* {
      key: 'id',
      label: 'ID kupca',
      render: (_, row) => {
        return row?.id || row?._id ? `#${row?._id || row?.id || ''}` : '-';
      },
    },*/
    /* {
      key: 'email',
      label: 'Email',
      render: (_, row) => {
        return row?.customer?.email || '-';
      },
    },*/
    {
      key: 'productName',
      label: 'Proizvod',
      render: (val) => val || 'Cela porudžbina',
    },
    {
      key: 'totalPrice',
      label: 'Osnovica za proviziju',
      render: (_, row) => {
        const total_price_render = row.commissionBase;
        return formatPriceWithCurrency(total_price_render || 0);
      },
    },
    {
      key: 'commissionDoctor',
      label: 'Provizija',
      render: (_, row) => {
        const commission_render = row.commissionDoctor;
        return formatPriceWithCurrency(commission_render || 0);
      },
    },
    {
      key: 'percent',
      label: 'Procenat provizije',
      render: (val) => (val != null ? `${val}%` : '-'),
    },
    {
      key: 'level',
      label: 'Nivo',
      render: (val) => (val != null ? val : '-'),
    },
    {
      key: 'chainOwnerName',
      label: 'Iskorišten kod od',
      render: (val) => val || '-',
    },
    {
      key: 'payoutStatus',
      label: 'Status provizije',
      render: (val, row) => formatPayoutStatusAsPill(row),
    },
    {
      key: 'createdAt',
      label: 'Datum kupovine',
      render: (value) => {
        const date_render = dayjs(value).isValid()
          ? dayjs(value).format('MM.DD.YYYY')
          : null;
        return date_render ? `${date_render}.` : '-';
      },
    },
  ];

  const handleFetchAllClinics = async () => {
    const { data, success, message } = await fetchAllClinics();

    if (!success) {
      toast.error(message || 'Error while fetching the data!');
      return;
    }

    // This was here if the clinic wants to add doctor to another clinic
    // const payload_data =
    //   data && data.clinics && data.clinics.length > 0
    //     ? data.clinics.map((i) => {
    //         return {
    //           label: i.name,
    //           value: i,
    //         };
    //       })
    //     : [];
    // const array_data = [{ label: 'Select', value: null }, ...payload_data];
    // setAllClinics(array_data);
    // Delete above if not needed at all
    const clinicId = data?.clinics?.find((f) => f.owner === user?._id);
    setClinicId(clinicId?._id || null);
    setClinic(clinicId);
  };

  const handleFetchMedicOrders = async () => {
    const params = {
      page: currentPage,
      limit: 10,
      search: debouncedSearch,
      createdFrom: orderFilter.createdFrom || undefined,
      createdTo: orderFilter.createdTo || undefined,
      payoutStatus: orderFilter.payoutStatus || undefined,
    };

    const { data, success, message } = await getMedicOrders(user._id, params);

    if (success) {
      setItems(data?.items || []);
      setTotalPages(data?.totalPages || 1);
      setTotalItems(data?.totalItems || 0);
    } else {
      console.error(message || 'Error while fetching the medics orders');
    }
  };

  const handleFetchDoctorReferral = async () => {
    const { data, success, message } = await getDoctorReferralLink(user?._id);

    if (success) {
      setUserReferral(data);
    } else {
      console.error(message || 'Error while fetching client summary');
    }
  };

  const handleFetchDoctorCommissionPercent = async () => {
    const { data, success } = await getDoctorReferralChain(user?._id);
    if (!success) return;
    // effectivePercent already accounts for the clinic-affiliation override
    // (a doctor tied to a clinic earns the clinic-adjusted rate, not their own).
    setDoctorCommissionPercent(data?.effectivePercent ?? null);
  };

  const handleFetchClinicsReferral = async () => {
    const { data, success, message } = await getClinicReferralLink(clinicId);

    if (success) {
      setUserReferral(data);
    } else {
      console.error(message || 'Error while fetching client summary');
    }
  };

  const handleFetchDoctorSummary = async () => {
    const { data, success, message } = await getDoctorSummary(user?._id);

    if (success) {
      setUserSummary(data);
    } else {
      console.error(message || 'Error while fetching client summary');
    }
  };

  const handleFetchClinicSummary = async () => {
    const { data, success, message } = await getClinicSummary(clinicId);

    if (success) {
      setUserSummary(data);
    } else {
      console.error(message || 'Error while fetching client summary');
    }
  };

  const handleFetchClinicMedics = async () => {
    const params = {
      page: currentPage,
      limit: 10,
      search: debouncedSearch,
    };

    const { data, success, message } = await getClinicMedics(clinicId, params);

    if (success) {
      setItems(data?.items || []);
      setTotalPages(data?.totalPages || 1);
      setTotalItems(data?.totalItems || 0);
    } else {
      console.error(message || 'Error while fetching the medics orders');
    }
  };

  // Clinic referral-commission awarding disabled (2026-08-17): clinic no longer
  // earns a cut, so its commission-ledger orders are no longer fetched.
  // const handleFetchClinicOrders = async () => {
  //   const { data, success, message } = await getClinicOrders(clinicId, {
  //     page: currentPageClinicOrders,
  //     limit: 10,
  //     createdFrom: orderFilter.createdFrom || undefined,
  //     createdTo: orderFilter.createdTo || undefined,
  //     payoutStatus: orderFilter.payoutStatus || undefined,
  //   });
  //
  //   if (success) {
  //     setClinicOrders(data?.items || []);
  //     setTotalPagesClinicOrders(data?.totalPages || 1);
  //     setCurrentPageClinicOrders(data?.currentPage || 1);
  //   } else {
  //     console.error(message || 'Error while fetching the clinic orders');
  //   }
  // };

  useEffect(() => {
    if (user?.role === 'doctor') {
      handleFetchMedicOrders();
      handleFetchDoctorSummary();
      handleFetchDoctorReferral();
      handleFetchDoctorCommissionPercent();
    } else if (user?.role === 'clinic_owner' && clinicId) {
      handleFetchClinicMedics();
      handleFetchClinicSummary();
      handleFetchClinicsReferral();
      // handleFetchClinicOrders();
    }
  }, [
    debouncedSearch,
    clinicId,
    currentPage,
    orderFilter.createdFrom,
    orderFilter.createdTo,
    orderFilter.payoutStatus,
  ]);

  useEffect(() => {
    handleFetchAllClinics();
  }, []);

  // Clinic owner's mentor rows (from /clinics/:id/doctors) carry the doctor's id as _id.
  const clinicMedicActions = [
    {
      key: 'view',
      label: 'Vidi detalje',
      icon: <img src="/assets/icons/view-icon.svg" alt="Vidi detalje" />,
      onClick: (row) =>
        navigate(`/medics/view/${row._id}`, {
          state: { backTo: '/clinic-medic' },
        }),
    },
  ];

  const renderTable = (
    state,
    emptyState = {
      icon: '/assets/icons/no-users.svg',
      title: 'Trenutno nema korisnika',
    },
    showFilter = true,
    rowClassName
  ) => {
    return (
      <TableSection
        columns={state.columns}
        actions={user?.role === 'clinic_owner' ? clinicMedicActions : []}
        data={state.data}
        emptyState={emptyState}
        showPagination={state.data?.length > 0}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        searchPlaceholder="Pretraži"
        rowClassName={rowClassName}
      />
    );
  };

  const label_to_render = user.role === 'doctor' ? 'Mentor' : 'Organizacija';
  const name_to_render =
    user.role === 'doctor'
      ? `${user.firstName || ''} ${user.lastName || ''}`
      : user?.clinic?.name || clinic?.name;

  const pillStyle = {
    padding: '4px 12px',
    borderRadius: '16px',
    fontSize: '14px',
    fontWeight: '500',
    display: 'inline-block',
  };

  return (
    <>
      <ContentLayout>
        <div className="info_container">
          <span>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <span className="label">{label_to_render}: </span>
                <span className="name">{name_to_render}</span>
              </div>
              <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {/* Clinic referral-commission awarding disabled (2026-08-17): the
                    percent badge is doctor-only now — clinics no longer earn a cut. */}
                {/* Commission-percentage pill disabled (2026-10-13), per request.
                {label_to_render === 'Mentor' && (
                  <span
                    style={{
                      ...pillStyle,
                      backgroundColor: '#ECFDF3',
                      color: '#027A48',
                    }}
                  >
                    {doctorCommissionPercent != null
                      ? `Provizija ${doctorCommissionPercent}%`
                      : 'Provizija -'}
                  </span>
                )} */}
                {/* {user.role === 'doctor' && (
                  <Button
                    className="btn-primary"
                    htmlType="button"
                    type="button"
                    onClick={handleOpenReferralModal}
                  >
                    Moj referral
                  </Button>
                )} */}
                {user.role === 'doctor' && (
                  <Button
                    className="btn-primary"
                    htmlType="button"
                    type="button"
                    onClick={() => setIsDirectReferralsModalOpen(true)}
                  >
                    Dodati mentori
                  </Button>
                )}
                {user.role === 'doctor' && (
                  <Button
                    className="btn-primary"
                    htmlType="button"
                    type="button"
                    onClick={() => setIsCreateMentorModalOpen(true)}
                  >
                    Dodaj mentora
                  </Button>
                )}
              </span>
            </div>
          </span>
        </div>

        <div className="table-wrapper referral_link_table_wrapper">
          <table className="referral_link_table">
            <thead>
              <tr>
                <th>
                  <div>
                    <span>Referral link</span>
                  </div>
                </th>
                {user?.role === 'doctor' && (
                  <th>
                    <div>
                      <span>Organizacija</span>
                    </div>
                  </th>
                )}
                <th>
                  <div>
                    <span>Ukupan broj kupovina</span>
                  </div>
                </th>
                <th>
                  <div>
                    <span>Ukupna vrednost kupovina</span>
                  </div>
                </th>
                <th>
                  <div>
                    <span>Ukupna provizija</span>
                  </div>
                </th>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td>
                  <div>
                    <span>
                      {userReferral && userReferral?.referralLink ? (
                        <Link
                          to={userReferral?.referralLink || '/clinic-medic'}
                        >
                          {userReferral?.referralCode &&
                          userReferral?.referralCode?.length > 40
                            ? `${userReferral?.referralCode?.substring(
                                0,
                                40
                              )}...`
                            : userReferral?.referralCode}
                        </Link>
                      ) : (
                        '-'
                      )}
                    </span>
                  </div>
                </td>
                {user?.role === 'doctor' && (
                  <td>
                    <div>
                      <span>{user?.clinic?.name || '-'}</span>
                    </div>
                  </td>
                )}
                <td>
                  <div>
                    <span>{userSummary?.totalOrders || 0}</span>
                  </div>
                </td>
                <td>
                  <div>
                    <span>
                      {formatPriceWithCurrency(userSummary?.totalRevenue || 0)}
                    </span>
                  </div>
                </td>
                <td>
                  <div>
                    <span>
                      {/* Clinic referral-commission awarding disabled
                          (2026-08-17): clinics no longer earn a cut. */}
                      {formatPriceWithCurrency(
                        userSummary?.totalCommissionDoctor || 0
                      )}
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {user?.role === 'doctor' && (
          <div style={{ marginTop: 32, marginBottom: 8 }}>
            <p className="title" style={{ marginBottom: 16 }}>
              Humanitarne donacije preko mojih narudžbina
            </p>
            <DoctorCharityEarnings doctorId={user?._id} />
          </div>
        )}

        {user?.role === 'doctor' && (
          <div style={{ marginTop: 32, marginBottom: 8 }}>
            <p className="title" style={{ marginBottom: 16 }}>
              Moji agenti
            </p>
            <MentorLinkedAgents mentorId={user?._id} />
          </div>
        )}

        <div className="clinic-medic-table-actions">
          <div className="label">
            <span>
              {user.role === 'doctor'
                ? 'Lista kupovina'
                : 'Lista mentora u organizaciji'}
            </span>
          </div>

          <div className="actions">
            <div className="search">
              <Search
                placeholder={'Pretraži'}
                className="search-input"
                allowClear
                enterButton
                onChange={(e) => onSearchUsers(e.target.value)}
                width="auto"
              />
            </div>

            {user.role === 'doctor' && (
              <>
                <ConfigProvider
                  theme={{
                    token: {
                      colorPrimary: '#005844',
                      controlOutline: 'rgba(0, 88, 68, 0.1)',
                      colorPrimaryHover: '#004636',
                    },
                    components: {
                      DatePicker: {
                        activeBorderColor: '#005844',
                        hoverBorderColor: '#005844',
                      },
                    },
                  }}
                >
                  <RangePicker
                    format="DD.MM.YYYY"
                    onChange={handleDateRangeChange}
                    placeholder={['Datum od', 'Datum do']}
                    allowClear
                  />
                </ConfigProvider>
                <Dropdown
                  menu={{
                    items: payoutStatusOptions.map((option) => ({
                      key: option.value,
                      label: option.label,
                      onClick: () => handlePayoutStatusSelect(option.value),
                    })),
                  }}
                  trigger={['click']}
                  placement="bottomLeft"
                  className="btn-filter-container"
                >
                  <Button
                    className={`btn-tertiary btn-filter ${orderFilter.payoutStatus ? 'active' : ''}`}
                  >
                    {activePayoutStatusLabel
                      ? `Provizija: ${activePayoutStatusLabel}`
                      : 'Status provizije'}
                    {orderFilter.payoutStatus && (
                      <img
                        src="/assets/icons/x-gray.svg"
                        title="Ukloni filter"
                        style={{
                          width: '10px',
                          height: '10px',
                          marginLeft: '5px',
                          cursor: 'pointer',
                        }}
                        alt="Ukloni filter"
                        onClick={handleClearPayoutStatus}
                      />
                    )}
                  </Button>
                </Dropdown>
              </>
            )}

            <div className="">
              <Button
                style={{ maxHeight: '38px', padding: '8px 16px' }}
                variant="primary"
                className="btn-primary"
                htmlType="button"
                type="button"
                onClick={handleTableAction}
              >
                {user.role === 'doctor' ? 'Export CSV file' : 'Dodaj mentora'}
              </Button>
            </div>
          </div>
        </div>

        {user.role === 'doctor' && (
          <div
            className="payoutLegend"
            style={{ justifyContent: 'flex-end', marginBottom: '12px' }}
          >
            {PAYOUT_STATUSES.map((s) => (
              <span key={s.key} className="legendItem">
                <span className={`payoutDot ${s.key}`} />
                {s.label}
              </span>
            ))}
          </div>
        )}

        {renderTable(
          {
            data: items,
            columns:
              user?.role === 'doctor'
                ? medic_table_columns
                : clinic_table_columns,
          },
          {
            title: user?.role === 'doctor' ? 'Nema Kupovina' : 'Nema Mentora',
            description:
              user?.role === 'doctor'
                ? 'Napravite novu Kupovinu'
                : 'Napravite novog Mentora',
          },
          true,
          user?.role === 'doctor'
            ? (row) => (row.payoutStatus === 'paid' ? 'row--paid' : '')
            : undefined
        )}

        {/* Clinic referral-commission awarding disabled (2026-08-17): the clinic's
            own "Porudžbine organizacije" commission-ledger orders section is
            commented out below — clinics no longer earn or get paid a commission cut.
        {user.role === 'clinic_owner' && (
          <>
            <div className="clinic-medic-table-actions">
              <div className="label">
                <span>Porudžbine organizacije</span>
              </div>

              <div className="actions">
                <ConfigProvider
                  theme={{
                    token: {
                      colorPrimary: '#005844',
                      controlOutline: 'rgba(0, 88, 68, 0.1)',
                      colorPrimaryHover: '#004636',
                    },
                    components: {
                      DatePicker: {
                        activeBorderColor: '#005844',
                        hoverBorderColor: '#005844',
                      },
                    },
                  }}
                >
                  <RangePicker
                    format="DD.MM.YYYY"
                    onChange={handleDateRangeChange}
                    placeholder={['Datum od', 'Datum do']}
                    allowClear
                  />
                </ConfigProvider>
                <Dropdown
                  menu={{
                    items: payoutStatusOptions.map((option) => ({
                      key: option.value,
                      label: option.label,
                      onClick: () => handlePayoutStatusSelect(option.value),
                    })),
                  }}
                  trigger={['click']}
                  placement="bottomLeft"
                  className="btn-filter-container"
                >
                  <Button
                    className={`btn-tertiary btn-filter ${orderFilter.payoutStatus ? 'active' : ''}`}
                  >
                    {activePayoutStatusLabel
                      ? `Provizija: ${activePayoutStatusLabel}`
                      : 'Status provizije'}
                    {orderFilter.payoutStatus && (
                      <img
                        src="/assets/icons/x-gray.svg"
                        title="Ukloni filter"
                        style={{
                          width: '10px',
                          height: '10px',
                          marginLeft: '5px',
                          cursor: 'pointer',
                        }}
                        alt="Ukloni filter"
                        onClick={handleClearPayoutStatus}
                      />
                    )}
                  </Button>
                </Dropdown>
              </div>
            </div>

            <div
              className="payoutLegend"
              style={{ justifyContent: 'flex-end', marginBottom: '12px' }}
            >
              {PAYOUT_STATUSES.map((s) => (
                <span key={s.key} className="legendItem">
                  <span className={`payoutDot ${s.key}`} />
                  {s.label}
                </span>
              ))}
            </div>

            <TableSection
              columns={clinic_orders_columns}
              actions={[]}
              data={clinicOrders}
              emptyState={{ title: 'Nema porudžbina' }}
              showPagination={clinicOrders.length > 0}
              currentPage={currentPageClinicOrders}
              totalPages={totalPagesClinicOrders}
              onPageChange={setCurrentPageClinicOrders}
              rowClassName={(row) =>
                row.payoutStatus === 'paid' ? 'row--paid' : ''
              }
            />
          </>
        )}
        */}
      </ContentLayout>

      <CreateMedicModal
        isOpen={isCreateMedicModalOpen}
        onClose={handleCreateMedicModalClose}
        onCreate={handleFetchClinicMedics}
        singleClinic={clinic?._id}
        // Organizations may only create new mentor accounts; the existing-user
        // picker would expose the platform's client base.
        allowExistingUser={false}
      />

      {user.role === 'doctor' && (
        <CreateMedicModal
          isOpen={isCreateMentorModalOpen}
          onClose={handleCreateMentorModalClose}
          onCreate={() => {}}
          singleClinic={user?.clinic?._id || user?.clinic || null}
          hideClinicSelect
          createFn={createReferredDoctor}
          allowExistingUser={false}
        />
      )}

      {user.role === 'doctor' && (
        <DoctorReferralModal
          isOpen={isReferralModalOpen}
          onClose={() => setIsReferralModalOpen(false)}
          doctor={doctorSelf}
        />
      )}

      {user.role === 'doctor' && (
        <DirectReferralsModal
          isOpen={isDirectReferralsModalOpen}
          onClose={() => setIsDirectReferralsModalOpen(false)}
          doctorId={user._id}
        />
      )}
    </>
  );
};

export default ClinicMedic;
